import {
    ReturnSlip,
    ReturnApiReturnCreateReturnPOSTRequest,
    ReturnStatusStatistic,
    ReturnOverviewDtoPageInfo,
    ReturnApiReturnSearchReturnsPOSTRequest,
    ReturnApiReturnGetReturnRequest,
    ReturnApiReturnUpdateReturnPOSTRequest,
    ReturnApiReturnCreateReturnCreditPOSTRequest,
    ReturnLogItem,
    ReturnApiReturnGetReturnLogsByReturnIdRequest,
    ReturnApiReturnAddCommentToReturnLogPOSTRequest,
    ReturnApiReturnUpdateReturnLogPinnedPATCHRequest,
    ReturnApiReturnDeleteReturnPOSTRequest,
    ReturnOrderGroup,
    MisPickSkuLookup,
} from 'returnCenter-api';
import { returnApi, orderAxiosInstance } from './api';
import { getLanguage } from './languageStore';

/**
 * Return service - handles return-related API calls
 * Maps to ReturnController endpoints
 */
export const returnService = {
    /**
     * Get a single return by ID
     */
    getReturn: async (request: ReturnApiReturnGetReturnRequest): Promise<ReturnSlip> => {
        const response = await returnApi.returnGetReturn({ language: getLanguage(), ...request });
        return response.data;
    },

    /**
     * Create a new return
     */
    createReturn: async (payload: ReturnApiReturnCreateReturnPOSTRequest): Promise<ReturnSlip> => {
        const response = await returnApi.returnCreateReturnPOST({ language: getLanguage(), ...payload });
        return response.data;
    },

    /**
     * Update an existing return
     */
    updateReturn: async (payload: ReturnApiReturnUpdateReturnPOSTRequest): Promise<ReturnSlip> => {
        const response = await returnApi.returnUpdateReturnPOST({ language: getLanguage(), ...payload });
        return response.data;
    },

    /**
     * Delete a return
     */
    deleteReturn: async (payload: ReturnApiReturnDeleteReturnPOSTRequest): Promise<boolean> => {
        const response = await returnApi.returnDeleteReturnPOST(payload);
        return response.data;
    },

    /**
     * Reopen a batch slip that is handled but not yet credited
     */
    reopenBatchSlip: async (returnId: number): Promise<ReturnSlip> => {
        const response = await orderAxiosInstance.post<ReturnSlip>(`/api/return/${returnId}/batch/reopen`, null, {
            params: { language: getLanguage() },
        });
        return response.data;
    },

    /**
     * Create return credit
     */
    createReturnCredit: async (payload: ReturnApiReturnCreateReturnCreditPOSTRequest): Promise<ReturnSlip> => {
        const response = await returnApi.returnCreateReturnCreditPOST({ language: getLanguage(), ...payload });
        return response.data;
    },

    /**
     * Search returns with filters
     */
    searchReturns: async (payload: ReturnApiReturnSearchReturnsPOSTRequest): Promise<ReturnOverviewDtoPageInfo> => {
        const response = await returnApi.returnSearchReturnsPOST({ language: getLanguage(), ...payload });
        return response.data;
    },

    /**
     * Get return statistics
     */
    getReturnStatistics: async (): Promise<Array<ReturnStatusStatistic>> => {
        const response = await returnApi.returnGetReturnStatisticsForCurrentCompany();
        return response.data;
    },

    /**
     * Get logs for a return
     */
    getReturnLogs: async (request: ReturnApiReturnGetReturnLogsByReturnIdRequest): Promise<Array<ReturnLogItem>> => {
        const response = await returnApi.returnGetReturnLogsByReturnId(request);
        return response.data;
    },

    /**
     * Add a log entry to a return
     */
    addReturnLog: async (payload: ReturnApiReturnAddCommentToReturnLogPOSTRequest): Promise<void> => {
        const response = await returnApi.returnAddCommentToReturnLogPOST(payload);
        return response.data;
    },

    /**
     * Update pinned status of a return log
     */
    updateReturnLogPinned: async (payload: ReturnApiReturnUpdateReturnLogPinnedPATCHRequest): Promise<void> => {
        const response = await returnApi.returnUpdateReturnLogPinnedPATCH(payload);
        return response.data;
    },

    getAvailableOrderLines: async (orderNumbers: string[]): Promise<ReturnOrderGroup[]> => {
        const response = await returnApi.returnGetAllOrderLinesPOST({
            requestBody: orderNumbers,
            language: getLanguage(),
        });
        return response.data;
    },

    /** Checks the SKU typed on a mis-pick line against the catalog, as crediting the return will. */
    lookupMisPickSku: async (sku: string): Promise<MisPickSkuLookup> => {
        const response = await returnApi.returnLookupMisPickSku({ sku, language: getLanguage() });
        return response.data;
    },
};
